import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  buildCompendiumUrl,
  getCompendiumOrigin,
  supportsVenueSubdomain,
} from "../../utils/venueUrl";

export default function PhonePreview({
  venueId,
  size,
  scale,
  liveTheme,
  getPreviewPayload,
  forcedOrigin,
  buildPreviewSrc,
  showRibbon = true,
}) {
  const iframeRef = useRef(null);
  const mountedRef = useRef(false);
  const [loaded, setLoaded] = useState(false);

  const origin = useMemo(() => {
    if (forcedOrigin) return forcedOrigin;

    if (process.env.NODE_ENV === "development") {
      return getCompendiumOrigin(venueId);
    }

    return getCompendiumOrigin(venueId);
  }, [forcedOrigin, venueId]);

  const src = useMemo(() => {
    if (!venueId) return "about:blank";

    if (typeof buildPreviewSrc === "function") {
      try {
        const custom = buildPreviewSrc({ origin, venueId });
        if (custom) return custom;
      } catch (err) {
        console.warn("[CMS] buildPreviewSrc failed, fallback to default", err);
      }
    }

    if (!origin) return "about:blank";

    try {
      const url = new URL("/?preview=1", `${origin}/`);
      if (!supportsVenueSubdomain(venueId)) {
        url.searchParams.set("venue_id", venueId);
      }
      return url.toString();
    } catch (err) {
      console.warn("[CMS] failed to build preview URL from origin, fallback to default", err);
      return buildCompendiumUrl(venueId, "/?preview=1");
    }
  }, [buildPreviewSrc, origin, venueId]);

  const targetOrigin = useMemo(() => {
    try {
      const urlOrigin = new URL(src).origin;
      const useWildcardForDev =
        urlOrigin.startsWith("http://localhost") ||
        urlOrigin.startsWith("http://127.") ||
        urlOrigin.startsWith("http://192.168.");

      return useWildcardForDev ? "*" : urlOrigin;
    } catch {
      return "*";
    }
  }, [src]);

  const readThemePayload = useCallback(() => {
    if (liveTheme && typeof liveTheme === "object") {
      if ("configuration" in liveTheme || "colors" in liveTheme) {
        return liveTheme;
      }

      return { configuration: liveTheme, colors: undefined };
    }

    const payload = getPreviewPayload?.();
    if (payload && typeof payload === "object") {
      if ("configuration" in payload || "colors" in payload) {
        return payload;
      }

      return { configuration: payload, colors: undefined };
    }

    return null;
  }, [getPreviewPayload, liveTheme]);

  const postTheme = useCallback(() => {
    const iframeEl = iframeRef.current;
    if (!venueId || !iframeEl?.contentWindow) return;

    const payload = readThemePayload();
    const configuration = payload?.configuration;
    if (!configuration || typeof configuration !== "object") return;

    try {
      iframeEl.contentWindow.postMessage(
        {
          type: "PREVIEW_THEME",
          payload: {
            configuration,
            colors: payload?.colors,
          },
        },
        targetOrigin
      );
    } catch (err) {
      console.warn("[CMS] postMessage failed:", err);
    }
  }, [readThemePayload, targetOrigin, venueId]);

  const handleLoad = useCallback(() => {
    if (mountedRef.current) {
      postTheme();
      return;
    }

    mountedRef.current = true;
    setLoaded(true);
    postTheme();
  }, [postTheme]);

  useEffect(() => {
    mountedRef.current = false;
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (!loaded) return undefined;

    const timer = setTimeout(() => postTheme(), 120);
    return () => clearTimeout(timer);
  }, [loaded, postTheme, size, scale, venueId, liveTheme]);

  const baseW = Number(size?.w) || 360;
  const baseH = Number(size?.h) || 780;
  const s = Number(scale) || 1;

  const wrapperStyle = {
    width: Math.max(100, Math.round(baseW * s)),
    height: Math.max(100, Math.round(baseH * s)),
    borderRadius: 22,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    position: "relative",
    overflow: "hidden",
    margin: "0 auto",
  };

  const canvasStyle = {
    width: baseW,
    height: baseH,
    transform: `scale(${s})`,
    transformOrigin: "top left",
    position: "absolute",
    left: 0,
    top: 0,
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
    background: "#0000000a",
    overflow: "hidden",
    borderRadius: 20,
  };

  const ribbonStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 2,
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.72)",
    color: "#fff",
    fontSize: 11,
    letterSpacing: 0.3,
    backdropFilter: "blur(6px)",
  };

  return (
    <div style={wrapperStyle}>
      {showRibbon ? <div style={ribbonStyle}>Live Preview</div> : null}
      <div style={canvasStyle}>
        <iframe
          ref={iframeRef}
          title="Live Venue Preview"
          src={src}
          style={{ width: "100%", height: "100%", border: 0, display: "block" }}
          sandbox={[
            "allow-scripts",
            "allow-same-origin",
            "allow-forms",
            "allow-pointer-lock",
            "allow-popups",
            "allow-top-navigation-by-user-activation",
          ].join(" ")}
          onLoad={handleLoad}
        />
      </div>
    </div>
  );
}
